import {
  getGetControlResultActivitiesQueryKey,
  getGetControlResultListPaginatedQueryKey,
  getGetControlResultQueryKey,
  useUpdateControlResultOwner as useUpdateControlResultOwnerMutation,
} from "@/spz-api/control-results/control-results";
import type {
  GetCompanyUsersResponseDto,
  GetControlResultDetailsResponseDto,
} from "@/spz-api/model";
import { useQueryClient } from "@tanstack/react-query";

export const useUpdateControlResultOwner = (
  controlId: string,
  companyId: string,
) => {
  const queryClient = useQueryClient();

  return useUpdateControlResultOwnerMutation({
    mutation: {
      onMutate: async ({ resultId: mutationResultId, data }) => {
        // Cancel any outgoing refetches to avoid overwriting optimistic update
        await queryClient.cancelQueries({
          queryKey: getGetControlResultQueryKey(controlId, mutationResultId),
        });

        // Snapshot the previous value
        const previousData =
          queryClient.getQueryData<GetControlResultDetailsResponseDto>(
            getGetControlResultQueryKey(controlId, mutationResultId),
          );

        // Try to find the full user data from the companies users cache
        let newOwnerData: GetControlResultDetailsResponseDto["data"]["owner"] =
          null;

        if (data.ownerId) {
          const usersQueryState = queryClient.getQueryCache().findAll({
            predicate: (query) =>
              query.queryKey[0] === `/companies/${companyId}/users`,
          });

          for (const query of usersQueryState) {
            const usersData = query.state.data as
              | GetCompanyUsersResponseDto
              | undefined;
            const matchedUser = usersData?.data?.find(
              (user) => user.id === data.ownerId,
            );

            if (matchedUser) {
              newOwnerData = {
                id: matchedUser.id,
                firstName: matchedUser.firstName,
                lastName: matchedUser.lastName,
                email: matchedUser.email,
                avatarUrl: matchedUser.avatarUrl ?? "",
              };
              break;
            }
          }

          // If we couldn't find full user data, use partial data with ID only
          // The full data will be fetched in onSettled
          if (!newOwnerData) {
            newOwnerData = {
              id: data.ownerId,
              firstName: "",
              lastName: "",
              email: "",
              avatarUrl: "",
            };
          }
        }

        // Optimistically update to the new value
        if (previousData) {
          queryClient.setQueryData<GetControlResultDetailsResponseDto>(
            getGetControlResultQueryKey(controlId, mutationResultId),
            {
              ...previousData,
              data: {
                ...previousData.data,
                owner: newOwnerData,
              },
            },
          );
        }

        // Return a context object with the snapshotted value
        return { previousData };
      },
      onError: (_err, { resultId: mutationResultId }, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousData) {
          queryClient.setQueryData(
            getGetControlResultQueryKey(controlId, mutationResultId),
            context.previousData,
          );
        }
      },
      onSettled: (_data, _error, { resultId: mutationResultId }) => {
        queryClient.invalidateQueries({
          queryKey: getGetControlResultListPaginatedQueryKey(controlId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetControlResultQueryKey(controlId, mutationResultId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetControlResultActivitiesQueryKey(
            controlId,
            mutationResultId,
          ),
        });
      },
    },
  });
};
